/**
 * Configuration API Service
 * Handles communication with the backend configuration API
 */

import type { SystemConfig } from '../types/config';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  details?: any;
}

class ConfigurationAPI {
  private baseUrl = `${API_BASE_URL}/config`;

  /**
   * Get current configuration from backend
   */
  async getConfiguration(): Promise<SystemConfig> {
    try {
      const response = await fetch(`${this.baseUrl}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get configuration: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to get configuration:', error);
      throw error;
    }
  }

  /**
   * Save configuration to backend
   */
  async saveConfiguration(config: SystemConfig): Promise<SystemConfig> {
    try {
      const response = await fetch(`${this.baseUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error(`Failed to save configuration: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to save configuration:', error);
      throw error;
    }
  }

  /**
   * Validate configuration
   */
  async validateConfiguration(config: SystemConfig): Promise<ValidationResult> {
    try {
      const response = await fetch(`${this.baseUrl}/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error(`Failed to validate configuration: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to validate configuration:', error);
      throw error;
    }
  }

  /**
   * Test database connection
   */
  async testDatabaseConnection(database: any): Promise<ConnectionTestResult> {
    try {
      const response = await fetch(`${this.baseUrl}/test-database`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(database),
      });

      if (!response.ok) {
        throw new Error(`Failed to test database connection: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to test database connection:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Test EBS connection
   */
  async testEBSConnection(ebs: any): Promise<ConnectionTestResult> {
    try {
      const response = await fetch(`${this.baseUrl}/test-ebs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(ebs),
      });

      if (!response.ok) {
        throw new Error(`Failed to test EBS connection: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to test EBS connection:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Test AIS connection
   */
  async testAISConnection(ais: any): Promise<ConnectionTestResult> {
    try {
      const response = await fetch(`${this.baseUrl}/test-ais`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(ais),
      });

      if (!response.ok) {
        throw new Error(`Failed to test AIS connection: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to test AIS connection:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Test API configuration
   */
  async testAPIConfiguration(api: any): Promise<ConnectionTestResult> {
    try {
      const response = await fetch(`${this.baseUrl}/test-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(api),
      });

      if (!response.ok) {
        throw new Error(`Failed to test API configuration: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to test API configuration:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Export configuration
   */
  async exportConfiguration(): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to export configuration: ${response.statusText}`);
      }

      return await response.text();
    } catch (error) {
      console.error('Failed to export configuration:', error);
      throw error;
    }
  }

  /**
   * Import configuration
   */
  async importConfiguration(config: SystemConfig): Promise<SystemConfig> {
    try {
      const response = await fetch(`${this.baseUrl}/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error(`Failed to import configuration: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to import configuration:', error);
      throw error;
    }
  }

  /**
   * Reset configuration to defaults
   */
  async resetConfiguration(): Promise<SystemConfig> {
    try {
      const response = await fetch(`${this.baseUrl}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to reset configuration: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to reset configuration:', error);
      throw error;
    }
  }

  /**
   * Get configuration history
   */
  async getConfigurationHistory(limit: number = 50): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/history?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get configuration history: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to get configuration history:', error);
      return [];
    }
  }

  /**
   * Clear configuration history
   */
  async clearConfigurationHistory(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/history`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to clear configuration history: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to clear configuration history:', error);
      throw error;
    }
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string {
    // In a real implementation, this would get the token from auth service
    return localStorage.getItem('authToken') || '';
  }
}

export const configurationAPI = new ConfigurationAPI();
